import React, { useState } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Button, ToggleButtonGroup,
  ToggleButton, Alert, CircularProgress, Divider, Chip, Avatar, Accordion,
  AccordionSummary, AccordionDetails, Grid,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import PetsIcon from '@mui/icons-material/Pets';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { collection, getDocs, getDoc, query, where, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { validateRut, cleanRut } from '../utils/validators';
import { generateLabReport } from '../services/reportGenerator';
import LabReport from '../components/LabReport';
import { calcAge } from '../models/imagingModel';
import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL } = COLORS;

const EXAM_TYPES = [
  { id: 'lab', label: 'Laboratorio', icon: ScienceIcon },
  { id: 'imaging', label: 'Imágenes', icon: ImageIcon },
];

const CLINIC_NAME = 'Hospital Clínico Veterinario UDLA';

const normalize = (rut) => cleanRut(rut);

const toDate = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d) ? null : d;
};

const formatDate = (ts) => {
  const d = toDate(ts);
  return d ? d.toLocaleDateString('es-CL') : '—';
};

const PublicResults = () => {
  const [rut, setRut] = useState('');
  const [examNumber, setExamNumber] = useState('');
  const [examType, setExamType] = useState('lab');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const collectionName = examType === 'lab' ? 'labOrders' : 'imagingOrders';
  const resultCollection = examType === 'lab' ? 'labResults' : 'imagingResults';

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!rut.trim() || !validateRut(rut)) {
      setError('Ingresa un RUT válido.');
      return;
    }
    if (!examNumber.trim()) {
      setError('Ingresa el número de exámen.');
      return;
    }

    setLoading(true);
    try {
      const orderSnap = await getDocs(query(
        collection(db, collectionName),
        where('orderNumber', '==', examNumber.trim().toUpperCase())
      ));

      if (orderSnap.empty) {
        setError('No se encontró un exámen con ese número.');
        setLoading(false);
        return;
      }

      const orderDoc = orderSnap.docs[0];
      const order = { id: orderDoc.id, ...orderDoc.data() };

      const [ownerDoc, patientDoc] = await Promise.all([
        order.ownerId ? getDoc(doc(db, 'owners', order.ownerId)) : Promise.resolve(null),
        order.patientId ? getDoc(doc(db, 'patients', order.patientId)) : Promise.resolve(null),
      ]);

      const owner = ownerDoc?.exists() ? { id: ownerDoc.id, ...ownerDoc.data() } : null;
      const patient = patientDoc?.exists() ? { id: patientDoc.id, ...patientDoc.data() } : null;

      if (!owner) {
        setError('No se pudieron validar los datos del tutor.');
        setLoading(false);
        return;
      }

      const ownerRut = normalize(owner.rut || '');
      const inputRut = normalize(rut);
      if (ownerRut !== inputRut) {
        setError('El RUT ingresado no coincide con el tutor del paciente.');
        setLoading(false);
        return;
      }

      let resultIds = [];
      if (examType === 'lab' && order.examResults) {
        resultIds = Object.values(order.examResults).map((e) => e.resultId).filter(Boolean);
      }
      if (order.resultId && !resultIds.includes(order.resultId)) {
        resultIds.push(order.resultId);
      }

      const resultSnaps = (await Promise.allSettled(
        resultIds.map((id) => getDoc(doc(db, resultCollection, id)))
      ))
        .filter((r) => r.status === 'fulfilled' && r.value?.exists())
        .map((r) => ({ id: r.value.id, ...r.value.data() }));

      // Disponibilidad: los labResults usan el campo `status`; los imagingResults
      // no lo tienen, se validan por la firma del informe o el estado de la orden.
      const orderClosed = ['approved', 'reported', 'delivered'].includes(order.status);
      const examResults = resultSnaps.filter((r) => (
        examType === 'lab'
          ? ['reported', 'approved'].includes(r.status)
          : Boolean(r.approvedAt || r.reportedAt) || orderClosed
      ));

      if (examResults.length === 0) {
        setError('El resultado de este exámen aún no está disponible.');
        setLoading(false);
        return;
      }

      const vet = order.vetId ? await getDoc(doc(db, 'professionals', order.vetId)).catch(() => null) : null;

      setResult({
        type: examType,
        order,
        results: examResults,
        patient,
        owner,
        vet: vet?.exists() ? { id: vet.id, ...vet.data() } : { nombre: order.vetName || '' },
        branch: { name: order.branchName || CLINIC_NAME },
      });
    } catch (err) {
      console.error(err);
      setError('Error al consultar el resultado. Por favor intenta más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLab = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      for (const examResult of result.results) {
        const blob = await generateLabReport({
          order: result.order,
          result: examResult,
          patient: result.patient,
          owner: result.owner,
          vet: result.vet,
          branch: result.branch,
        });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error(err);
      setError('No se pudo generar el PDF del informe.');
    } finally {
      setDownloading(false);
    }
  };

  // Fuerza la descarga del archivo; si el navegador bloquea el blob (CORS), lo abre en pestaña
  const downloadFile = async (url, fileName) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  };

  const handleDownloadAllImages = async () => {
    const files = (result?.order?.files || []).filter((f) => (f.fileType || '').startsWith('image/'));
    setDownloading(true);
    try {
      for (const [idx, file] of files.entries()) {
        await downloadFile(file.downloadURL, file.fileName || `imagen_${idx + 1}.jpg`);
      }
    } finally {
      setDownloading(false);
    }
  };

  const renderImagingResult = () => {
    const { order, results, patient, owner } = result;
    const imagingResult = results[0];
    const findings = imagingResult?.findings || [];
    const hasCritical = findings.some((f) => f.criticalFinding);
    const imageFiles = (order.files || []).filter((f) => (f.fileType || '').startsWith('image/'));
    const otherFiles = (order.files || []).filter((f) => !(f.fileType || '').startsWith('image/'));
    const reportPdfUrl = imagingResult?.reportFile?.downloadURL;

    return (
      <Box sx={{ overflowX: 'auto', mt: 3 }}>
        {/* Panel de descargas (no se imprime) */}
        <Paper
          elevation={2}
          className="no-print"
          sx={{ p: 2.5, borderRadius: 3, width: '210mm', minWidth: '210mm', mx: 'auto', mb: 2, bgcolor: '#fff' }}
        >
          <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} gutterBottom>
            Descargas disponibles
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {reportPdfUrl ? (
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => downloadFile(reportPdfUrl, imagingResult.reportFile?.fileName || `informe_${order.orderNumber}.pdf`)}
                sx={{ bgcolor: PRIMARY, fontWeight: 600 }}
              >
                Descargar informe PDF
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => window.print()}
                sx={{ bgcolor: PRIMARY, fontWeight: 600 }}
              >
                Descargar informe (imprimir)
              </Button>
            )}
            {imageFiles.length > 0 && (
              <Button
                variant="outlined"
                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadAllImages}
                disabled={downloading}
              >
                Descargar {imageFiles.length} imagen{imageFiles.length > 1 ? 'es' : ''}
              </Button>
            )}
          </Box>

          {imageFiles.length === 0 && otherFiles.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Esta orden no tiene imágenes disponibles para descarga.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {imageFiles.map((file, idx) => (
                <Box
                  key={file.id || idx}
                  sx={{ width: 120, border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}
                >
                  <Box
                    component="a"
                    href={file.downloadURL}
                    target="_blank"
                    rel="noopener"
                    sx={{ display: 'block', height: 100, bgcolor: '#0B0F14' }}
                  >
                    <img
                      src={file.downloadURL}
                      alt={file.fileName || `Imagen ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Button
                    fullWidth
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => downloadFile(file.downloadURL, file.fileName || `imagen_${idx + 1}.jpg`)}
                    sx={{ fontSize: '0.68rem', borderRadius: 0 }}
                  >
                    Descargar
                  </Button>
                </Box>
              ))}
              {otherFiles.map((file, idx) => (
                <Button
                  key={file.id || `o${idx}`}
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadFile(file.downloadURL, file.fileName || `archivo_${idx + 1}`)}
                >
                  {file.fileName || `Archivo ${idx + 1}`}
                </Button>
              ))}
            </Box>
          )}
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, width: '210mm', minWidth: '210mm', mx: 'auto', bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color={PRIMARY}>
              Informe de Imágenes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Orden {order.orderNumber} · {formatDate(imagingResult.reportedAt || imagingResult.approvedAt)}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" className="no-print" startIcon={<PictureAsPdfIcon />} onClick={() => window.print()}>
            Descargar / Imprimir
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: '#F4F6F8', borderRadius: 2 }}>
          <Avatar sx={{ bgcolor: TEAL }}><PetsIcon /></Avatar>
          <Box>
            <Typography fontWeight={700}>{patient?.name || order.patientName || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {order.patientSpecies || patient?.species || '—'} · {order.patientBreed || patient?.breed || '—'} · {calcAge(patient?.birthDate || order.patientBirthdate)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Tutor: {owner?.name || order.ownerName || '—'}
            </Typography>
          </Box>
        </Box>

        {hasCritical && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este informe contiene hallazgos críticos. Contacta al médico veterinario para una interpretación.
          </Alert>
        )}

        {findings.length === 0 ? (
          <Alert severity="info">No se han registrado hallazgos para esta orden.</Alert>
        ) : (
          findings.map((f, idx) => (
            <Accordion key={idx} defaultExpanded={findings.length === 1} sx={{ mb: 1, border: '1px solid #E2E8F0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={700} color={PRIMARY}>
                    {idx + 1}. {f.examTypeName || 'Estudio'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {f.modality || '—'} · {f.bodyRegion || '—'}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {f.technique && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700}>Técnica</Typography>
                    <Typography variant="body2">{f.technique}</Typography>
                  </Box>
                )}
                {f.findings && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700}>Hallazgos</Typography>
                    <Typography variant="body2" whiteSpace="pre-line">{f.findings}</Typography>
                  </Box>
                )}
                {f.impression && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700}>Impresión diagnóstica</Typography>
                    <Typography variant="body2" whiteSpace="pre-line">{f.impression}</Typography>
                  </Box>
                )}
                {f.recommendation && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700}>Recomendación</Typography>
                    <Typography variant="body2" whiteSpace="pre-line">{f.recommendation}</Typography>
                  </Box>
                )}
                {f.criticalFinding && f.criticalFindingText && (
                  <Alert severity="warning" sx={{ mt: 1 }}>{f.criticalFindingText}</Alert>
                )}
                {Array.isArray(f.measurements) && f.measurements.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={700}>Mediciones</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {f.measurements.map((m, i) => (
                        <Chip key={i} size="small" label={`${m.structure}: ${m.value} ${m.normal ? '' : '(fuera de rango)'}`} color={m.normal ? 'default' : 'warning'} />
                      ))}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {imagingResult.overallImpression && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#EFF6FF' }}>
            <Typography variant="caption" fontWeight={700}>Conclusión general</Typography>
            <Typography variant="body2" whiteSpace="pre-line">{imagingResult.overallImpression}</Typography>
          </Paper>
        )}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Este informe fue generado por {imagingResult.approvedByName || imagingResult.reportedByName || 'HCV UDLA'}.
        </Typography>
      </Paper>
    </Box>
    );
  };

  const renderLabResult = () => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color={PRIMARY}>
          Informe de Laboratorio ({result.results.length} exámen{result.results.length > 1 ? 'es' : ''})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadLab}
          disabled={downloading}
        >
          {downloading ? 'Generando...' : 'Descargar PDF'}
        </Button>
      </Box>
      {result.results.map((examResult, idx) => (
        <Box key={examResult.id} sx={{ overflowX: 'auto', bgcolor: '#fff', borderRadius: 2, border: '1px solid #E2E8F0', mb: 3 }}>
          <LabReport
            result={examResult}
            order={result.order}
            patient={result.patient}
            owner={result.owner}
            vet={result.vet}
            branch={result.branch}
            mode="print"
          />
        </Box>
      ))}
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
      <style>{`@media print { .no-print { display:none!important; } }`}</style>
      <Container maxWidth="md">
        <Box sx={{ displayPrint: 'none' }}>
          <Typography variant="h4" fontWeight={800} color={PRIMARY} textAlign="center" gutterBottom>
            {CLINIC_NAME}
          </Typography>
          <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
            Consulta de resultados
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Ingresa tu RUT y el número de orden para revisar el resultado de tu mascota.
          </Typography>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box component="form" onSubmit={handleSearch}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <ToggleButtonGroup
                    fullWidth
                    color="primary"
                    value={examType}
                    exclusive
                    onChange={(_, v) => v && setExamType(v)}
                  >
                    {EXAM_TYPES.map((t) => {
                      const Icon = t.icon;
                      return (
                        <ToggleButton key={t.id} value={t.id}>
                          <Icon sx={{ fontSize: 18, mr: 0.5 }} />
                          {t.label}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="RUT del tutor"
                    placeholder="12345678-9"
                    value={rut}
                    onChange={(e) => setRut(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="Número de exámen"
                    placeholder={examType === 'lab' ? 'LAB-20260826-1000' : 'IMG-20260826-1000'}
                    value={examNumber}
                    onChange={(e) => setExamNumber(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    disabled={loading}
                    sx={{ height: '100%' }}
                  >
                    Buscar
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </Paper>
        </Box>

        {result && (
          <Box sx={{ mt: 3 }}>
            {result.type === 'lab' ? renderLabResult() : renderImagingResult()}
          </Box>
        )}

        <Divider sx={{ my: 4, displayPrint: 'none' }} />

        <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ displayPrint: 'none' }}>
          © HCV UDLA · Sistema de Gestión Hospitalaria Sphere
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicResults;
